/* MAG RECIPE - ein UI für Metaobject- und historische HTML-Rezepte.
   Bestandskarten werden progressiv in exakt dieselbe semantische Struktur
   überführt. So bleiben Inhalt und URLs erhalten, während CI, UX und
   Recipe-JSON-LD zentral aus einer Darstellung kommen.

   05.08.2026 - drei Korrekturen:
   1. Der Portionsrechner hat einheitenblind gerundet. Aus einem Päckchen
      Backpulver wurden bei acht auf neun Portionen 1,13 Pck., aus einem
      halben Esslöffel Sojamehl 0,13 EL. Mengen werden jetzt nach Art der
      Einheit gerundet und als Bruch gesetzt, wie man es aufschreibt.
   2. Die Portionsänderung war für Hilfstechnik unsichtbar. Vierzehn
      Zahlen wechselten lautlos. WCAG 2.2 SC 4.1.3. Es gibt jetzt eine
      Ansage.
   3. Das umgewandelte Modul stand im Dokument dort, wo die alte Karte
      stand - das ist richtig so. Die Reihenfolge wird nicht mehr per CSS
      verschoben, sondern hier geprüft und notfalls im DOM korrigiert.
*/

(function () {
  if (window.__magRecipeBooted) return;
  window.__magRecipeBooted = true;

  /* ---------------------------------------------------------------- Mengen

     Eine Menge ist keine Kommazahl. 250 g werden zu 281 g, nicht zu
     280,75 g. Ein Päckchen bleibt ein Päckchen oder wird zu zweien, es
     wird nie zu 1,13. Ein Esslöffel geht in Vierteln, weil man Viertel
     löffeln kann. Das ist die ganze Regel.
  */

  var GANZ = ['pck', 'pkg', 'päckchen', 'paeckchen', 'prise', 'prisen', 'blatt',
    'ei', 'eier', 'dose', 'dosen', 'bund', 'zehe', 'zehen', 'handvoll'];
  var unitKind = function (unit) {
    var u = String(unit || '').toLowerCase().replace(/\.$/, '').trim();
    if (u === 'g' || u === 'ml' || u === 'mg') return 'fein';
    if (u === 'kg' || u === 'l' || u === 'dl' || u === 'cl') return 'gross';
    if (u === 'el' || u === 'tl') return 'loeffel';
    if (u === '') return 'zahl';
    /* Ein halbes Päckchen Backpulver gibt es im Regal nicht. Der Kopf
       dieser Datei behauptete das seit heute Morgen, die Rundung hat es
       nicht eingehalten - gemessen "½ Pck." bei einer Portion. */
    if (GANZ.indexOf(u) >= 0) return 'ganz';
    return 'stueck';
  };

  var roundQty = function (value, kind) {
    if (!isFinite(value) || value <= 0) return value;
    if (kind === 'fein') {
      if (value >= 100) return Math.round(value / 5) * 5;
      if (value >= 10) return Math.round(value);
      return Math.max(0.5, Math.round(value * 2) / 2);
    }
    if (kind === 'gross') {
      if (value >= 10) return Math.round(value * 10) / 10;
      return Math.round(value * 100) / 100;
    }
    if (kind === 'ganz') return Math.max(1, Math.round(value));
    if (kind === 'loeffel') return Math.max(0.25, Math.round(value * 4) / 4);
    if (kind === 'zahl') {
      if (value >= 10) return Math.round(value);
      return Math.max(0.5, Math.round(value * 2) / 2);
    }
    return Math.max(0.5, Math.round(value * 2) / 2);
  };

  var FRACTIONS = [[0.25, '¼'], [0.5, '½'], [0.75, '¾'], [1 / 3, '⅓'], [2 / 3, '⅔']];

  var formatQty = function (value) {
    if (!isFinite(value)) return '';
    var whole = Math.floor(value + 1e-9);
    var rest = value - whole;
    for (var i = 0; i < FRACTIONS.length; i++) {
      if (Math.abs(FRACTIONS[i][0] - rest) < 0.02) {
        return (whole > 0 ? whole : '') + FRACTIONS[i][1];
      }
    }
    if (rest < 0.02) return String(whole);
    return String(Math.round(value * 100) / 100).replace('.', ',');
  };

  /* "1 Portionen" ist kein Deutsch, weder auf dem Schirm noch in der
     Ansage für Hilfstechnik. */
  var SINGULAR = { 'portionen': 'Portion', 'gläser': 'Glas', 'glaeser': 'Glas',
    'personen': 'Person', 'drinks': 'Drink', 'stücke': 'Stück', 'tassen': 'Tasse' };
  var PLURAL = { 'portion': 'Portionen', 'glas': 'Gläser', 'person': 'Personen',
    'drink': 'Drinks', 'tasse': 'Tassen' };
  var singular = function (label) {
    var key = String(label || '').toLowerCase().trim();
    return SINGULAR[key] || label;
  };
  /* Der Steller startet bei Cocktails auf 1 und traegt dann "Drink".
     Wer hochzaehlt, bekam "4 Drink". Beide Formen muessen bekannt sein,
     nicht nur die, die zufaellig im Markup steht. */
  var plural = function (label) {
    var key = String(label || '').toLowerCase().trim();
    return PLURAL[key] || label;
  };

  class MagRecipe extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.base = parseFloat(this.dataset.base) || 1;
      this.current = this.base;
      this.wakeLock = null;
      this.servingsEl = this.querySelector('[data-servings]');

      /* Die Einheit für die Ansage steht neben der Zahl im Steller. Damit
         sie beim Wechsel zwischen ein und mehreren mitläuft, bekommt sie
         einen eigenen Behälter, sofern das Markup keinen mitbringt. */
      var valBox = this.querySelector('.mag-recipe__servings-val');
      var unitBox = this.querySelector('[data-servings-unit]');
      if (valBox && !unitBox && this.servingsEl) {
        var rest = '';
        var node = this.servingsEl.nextSibling;
        while (node) {
          var gone = node;
          rest += gone.textContent || '';
          node = node.nextSibling;
          gone.remove();
        }
        unitBox = document.createElement('span');
        unitBox.setAttribute('data-servings-unit', '');
        unitBox.textContent = rest.replace(/\s+/g, ' ').trim();
        valBox.appendChild(document.createTextNode(' '));
        valBox.appendChild(unitBox);
      }
      var valText = unitBox
        ? String(unitBox.textContent || '').replace(/\s+/g, ' ').trim()
        : (valBox ? String(valBox.textContent || '').replace(/\s+/g, ' ').trim() : '');
      var rawUnit = valText.replace(/^[\d.,]+\s*/, '') || 'Portionen';
      this.unitOne = singular(rawUnit);
      this.unitMany = plural(rawUnit);
      this.unitLabel = this.unitMany;

      /* Der Eckdaten-Eintrag "Ergibt" wiederholt, was der Steller sagt.
         Bis 05.08.2026 blieb er auf dem Startwert stehen: oben stand
         12 Portionen, vierzig Pixel darüber weiterhin 8. */
      this.yieldFact = null;
      this.querySelectorAll('.mag-recipe__facts div').forEach((row) => {
        var dt = row.querySelector('dt');
        var dd = row.querySelector('dd');
        if (!dt || !dd) return;
        if (/^(ergibt|portionen|menge)$/i.test(dt.textContent.replace(/\s+/g, ' ').trim())) {
          this.yieldFact = dd;
        }
      });

      /* Ansagefeld für Hilfstechnik. Steht im Markup nicht drin, weil es
         zwei Quellen gibt - Liquid und die Umwandlung hier. Also einmal
         zentral. */
      this.live = this.querySelector('[data-recipe-live]');
      if (!this.live && this.servingsEl) {
        this.live = document.createElement('p');
        this.live.className = 'asif-sr-live';
        this.live.setAttribute('role', 'status');
        this.live.setAttribute('aria-live', 'polite');
        this.live.setAttribute('data-recipe-live', 'true');
        var bar = this.querySelector('.mag-recipe__toolbar') || this;
        bar.appendChild(this.live);
      }

      this.down = this.querySelector('[data-servings-down]');
      this.up = this.querySelector('[data-servings-up]');
      if (this.down) this.down.addEventListener('click', () => this.scale(-1));
      if (this.up) this.up.addEventListener('click', () => this.scale(1));
      var printBtn = this.querySelector('[data-print]');
      if (printBtn) printBtn.addEventListener('click', () => window.print());
      this.maximum = Math.max(24, this.base * 4);
      this.updateButtons();
      this.markUnscaled();

      /* ml oder cl. Faktor 10, keine Rundungsfrage - der billigste
         echte Gewinn fuer die 29 Cocktailartikel. Hinter der Bar wird in
         cl gedacht, zu Hause in ml. */
      this.centi = false;
      var unitToggle = this.querySelector('[data-unit-toggle]');
      if (unitToggle) {
        /* Ein Umschaltknopf mit fester Beschriftung und ehrlichem Zustand.
           Die frueher wechselnde Beschriftung ("in ml" wenn cl aktiv war)
           hat zwei Regeln gleichzeitig gebrochen: der sichtbare Text stand
           nicht im zugaenglichen Namen (SC 2.5.3), und nach dem ersten Klick
           verschwand aria-pressed, der Knopf verlor also seinen Zustand
           (SC 4.1.2). Jetzt heisst er immer "in cl" und sagt per aria-pressed,
           ob die cl-Ansicht an ist. */
        unitToggle.textContent = 'in cl';
        unitToggle.setAttribute('aria-pressed', 'false');
        unitToggle.setAttribute('aria-label', 'in cl - Mengen in Zentilitern anzeigen');
        unitToggle.addEventListener('click', () => {
          this.centi = !this.centi;
          unitToggle.setAttribute('aria-pressed', this.centi ? 'true' : 'false');
          this.render();
          if (this.live) {
            this.live.textContent = this.centi
              ? 'Mengen in Zentilitern.'
              : 'Mengen in Millilitern.';
          }
        });
      }

      const cookBtn = this.querySelector('[data-cook-mode]');
      if (cookBtn && 'wakeLock' in navigator) {
        cookBtn.hidden = false;
        cookBtn.setAttribute('aria-pressed', 'false');
        cookBtn.addEventListener('click', () => this.toggleWakeLock(cookBtn));
        document.addEventListener('visibilitychange', () => {
          if (this.wakeLock !== null && document.visibilityState === 'visible') {
            this.requestWakeLock(cookBtn);
          }
        });
      }
    }

    /* Ein Knopf, der aussieht wie bedienbar und nichts tut, ist ein
       kaputter Knopf. Bei 1 Portion gibt es kein Weniger.

       Aber: disabled nimmt den Knopf aus der Tabfolge. Wer sich mit der
       Tastatur bis zum Maximum hochklickt, verliert in dem Moment den Fokus
       ans Dokument und landet beim naechsten Tab wieder ganz oben (SC 2.4.3).
       Deshalb nur aria-disabled. Die Grenze faengt scale() ohnehin ab. */
    updateButtons() {
      if (this.down) {
        this.down.setAttribute('aria-disabled', this.current <= 1 ? 'true' : 'false');
      }
      if (this.up) {
        this.up.setAttribute('aria-disabled', this.current >= this.maximum ? 'true' : 'false');
      }
    }

    render() {
      const factor = this.current / this.base;
      this.querySelectorAll('[data-qty]').forEach((el) => {
        const num = parseFloat(String(el.dataset.qty).replace(',', '.'));
        if (Number.isNaN(num)) return;
        const scope = el.parentElement || el;
        const unitEl = scope.querySelector('.mag-recipe__unit');
        /* Die Einheit aus dem Quelltext ist die Wahrheit; was im Kasten
           steht, kann der Umschalter geaendert haben. */
        let unit = '';
        if (unitEl) {
          if (!unitEl.dataset.unit) unitEl.dataset.unit = String(unitEl.textContent || '').trim();
          unit = unitEl.dataset.unit;
        }
        const value = num * factor;
        if (this.centi && /^ml$/i.test(unit)) {
          if (unitEl) unitEl.textContent = 'cl';
          /* Hinter der Bar steht 4,5 cl, nicht 4½ cl. Der Bruchsatz aus
             formatQty gehoert ins Backrezept, nicht in eine metrische
             Einheit. */
          var cl = Math.round(value) / 10;
          el.textContent = (Math.round(cl * 10) / 10).toString().replace('.', ',');
          return;
        }
        if (unitEl && unitEl.textContent !== unit) unitEl.textContent = unit;
        el.textContent = formatQty(roundQty(value, unitKind(unit)));
      });
    }

    /* Zeilen ohne data-qty rechnen nicht mit - Bereichsangaben wie
       "100-200 g" zum Beispiel. Sobald der Faktor nicht 1 ist, wird das
       an der Zeile gesagt statt verschwiegen. */
    markUnscaled() {
      const scaled = this.current !== this.base;
      this.classList.toggle('mag-recipe--scaled', scaled);
      this.querySelectorAll('.mag-recipe__item').forEach((item) => {
        const hasQty = !!item.querySelector('[data-qty]');
        const hasNumber = /\d/.test(item.textContent || '');
        if (!hasQty && hasNumber) item.setAttribute('data-unscaled', '');
        else item.removeAttribute('data-unscaled');
      });
    }

    scale(dir) {
      const next = this.current + dir;
      if (next < 1 || next > this.maximum) return;
      this.current = next;
      if (this.servingsEl) this.servingsEl.textContent = this.current;
      this.render();
      /* Eine Portion ist keine Portionen. */
      const label = this.current === 1 ? this.unitOne : this.unitMany;
      const unitBox = this.querySelector('[data-servings-unit]');
      if (unitBox) unitBox.textContent = label;
      /* Die Eckdaten dürfen dem Steller nicht widersprechen. */
      if (this.yieldFact) this.yieldFact.textContent = this.current + ' ' + label;
      this.updateButtons();
      this.markUnscaled();
      if (this.live) {
        this.live.textContent = this.current + ' ' + label + ', Mengen angepasst.';
      }
    }

    async requestWakeLock(btn) {
      try {
        this.wakeLock = await navigator.wakeLock.request('screen');
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        btn.textContent = 'Display bleibt an';
        this.wakeLock.addEventListener('release', () => {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-pressed', 'false');
          btn.textContent = 'Display anlassen';
        });
      } catch (error) {
        console.warn('mag-recipe wakelock:', error);
      }
    }

    async toggleWakeLock(btn) {
      if (this.wakeLock) {
        await this.wakeLock.release();
        this.wakeLock = null;
        return;
      }
      this.requestWakeLock(btn);
    }
  }

  if (!customElements.get('mag-recipe')) {
    customElements.define('mag-recipe', MagRecipe);
  }

  const clean = (value) => (value || '').replace(/\s+/g, ' ').trim();
  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const numericQuantity = (value) => {
    const fractions = { '¼': .25, '½': .5, '¾': .75, '⅓': 1 / 3, '⅔': 2 / 3, '⅛': .125 };
    const normalized = clean(value).replace(/^ca\.\s*/i, '');
    if (fractions[normalized]) return fractions[normalized];
    if (/^\d+\s*\/\s*\d+$/.test(normalized)) {
      const [a, b] = normalized.split('/').map(Number);
      return b ? a / b : null;
    }
    const number = Number(normalized.replace(',', '.'));
    return Number.isFinite(number) ? number : null;
  };

  /* Inline-Auszeichnung überlebt die Umwandlung.
     Bis 05.08.2026 lief jede Zutat durch escapeHtml. Damit verschwanden
     die Produktlinks im Rezept - gemessen null Verweise im Modul gegen
     zwei im Quelltext. Eine Zutat darf verlinken, mehr nicht. */
  const ALLOWED = { A: ['href', 'title'], STRONG: [], B: [], EM: [], I: [], ABBR: ['title'], SUP: [], SUB: [] };
  const sanitize = (node) => {
    const out = document.createDocumentFragment();
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        out.appendChild(document.createTextNode(child.textContent));
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;
      const allow = ALLOWED[child.tagName];
      if (!allow) { out.appendChild(sanitize(child)); return; }
      const el = document.createElement(child.tagName.toLowerCase());
      allow.forEach((name) => {
        const value = child.getAttribute(name);
        if (!value) return;
        if (name === 'href' && !/^(https?:|\/|#|mailto:)/i.test(value.trim())) return;
        el.setAttribute(name, value);
      });
      el.appendChild(sanitize(child));
      out.appendChild(el);
    });
    return out;
  };
  const firstTextNode = (node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE && child.textContent.trim()) return child;
      if (child.nodeType === Node.ELEMENT_NODE) {
        const found = firstTextNode(child);
        if (found) return found;
      }
    }
    return null;
  };
  const escapeRe = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const parseIngredient = (source) => {
    const element = typeof source === 'string' ? null : source;
    const text = clean(element ? element.textContent : source);
    const match = text.match(/^((?:ca\.\s*)?(?:\d+(?:[.,]\d+)?|\d+\s*\/\s*\d+|[¼½¾⅓⅔⅛]))\s*(kg|mg|g|ml|cl|dl|l|EL|TL|Pck\.?|Pkg\.?|Prisen?|Stück|Bund|Dosen?|Tassen?|Zehen?|Scheiben?|Zweige?|Handvoll)?\s+(.*)$/i);
    if (!match) return { raw: text, qty: '', unit: '', name: text, numeric: null, node: element };
    return {
      raw: text,
      qty: clean(match[1]),
      unit: clean(match[2]),
      name: clean(match[3]),
      numeric: numericQuantity(match[1]),
      node: element
    };
  };

  /* Aus der Quellzeile den Namen als Knoten bauen: Menge und Einheit vorn
     abschneiden, den Rest samt Auszeichnung behalten. Passt der Anfang
     nicht auf die erkannte Menge, bleibt es beim reinen Text. */
  const nameFragment = (item) => {
    if (!item.node) return null;
    const frag = sanitize(item.node);
    if (!item.qty) return frag;
    const first = firstTextNode(frag);
    if (!first) return null;
    const re = new RegExp('^\\s*' + escapeRe(item.qty) + '\\s*' + (item.unit ? escapeRe(item.unit) : '') + '\\s*');
    if (!re.test(first.textContent.replace(/\s+/g, ' '))) return null;
    first.textContent = first.textContent.replace(/\s+/g, ' ').replace(re, '');
    return frag;
  };

  /* Glaeser, die in einem Cocktailrezept vorkommen. Das Glas ist beim
     Drink eine Vorbereitungsentscheidung - vorkuehlen, richtige Form -
     und gehoert deshalb nach oben, nicht in einen Schritt. Difford's
     fuehrt es als eigenes Feld ganz vorn. */
  const GLASS = /\b(coupette|coupe|martiniglas|nick\s*&?\s*nora|tumbler|old[- ]fashioned|rocks|highball|collins|longdrink|julep|sour ?glas|weinglas|schwenker|becher|krug|shot|flöte|flute|hurricane|tiki|copa)\b/i;

  const factParts = (text, index) => {
    const value = clean(text);
    const colon = value.indexOf(':');
    if (colon > 0) return [value.slice(0, colon), value.slice(colon + 1)];
    if (/^\s*ca?\.?\s*\d+\s*(min|minuten|std|stunden?|h)\b/i.test(value)) return ['Zeit', value];
    if (/leicht|mittel|schwer|einfach|aufwändig/i.test(value)) return ['Level', value];
    if (GLASS.test(value)) return ['Glas', value];
    if (/portion|stück|gläser|glas|drink|cocktail|person|ergibt/i.test(value)) return ['Ergibt', value];
    return [`Info ${index + 1}`, value];
  };

  /* Zeitangaben stehen in den Eckdaten als Text. Google will sie als
     ISO-8601-Dauer im Recipe-Objekt. Bis 05.08.2026 fehlten sie in der
     erzeugten Auszeichnung, obwohl sie auf der Seite sichtbar waren. */
  const minutesFrom = (text) => {
    const value = clean(text);
    const hours = value.match(/(\d+(?:[.,]\d+)?)\s*(?:std|stunden?|h)\b/i);
    const mins = value.match(/(\d+)\s*(?:min|minuten?)\b/i);
    let total = 0;
    if (hours) total += Math.round(Number(hours[1].replace(',', '.')) * 60);
    if (mins) total += Number(mins[1]);
    return total > 0 ? total : null;
  };

  const isoDuration = (minutes) => {
    if (!minutes) return undefined;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return 'PT' + (h ? h + 'H' : '') + (m ? m + 'M' : (h ? '' : '0M'));
  };

  const timesFrom = (facts) => {
    let prep = null, cook = null, total = null;
    facts.forEach((fact) => {
      const value = clean(fact);
      const minutes = minutesFrom(value);
      if (!minutes) return;
      if (/vorbereit|arbeitszeit|prep/i.test(value)) prep = prep || minutes;
      else if (/back|koch|gar|cook|zubereitungszeit/i.test(value)) cook = cook || minutes;
      else if (/gesamt|insgesamt|total/i.test(value)) total = total || minutes;
      else if (/ruhe|kühl|kuehl|zieh|warte/i.test(value)) total = total || minutes;
    });
    if (!total && (prep || cook)) total = (prep || 0) + (cook || 0);
    return {
      prepTime: isoDuration(prep),
      cookTime: isoDuration(cook),
      totalTime: isoDuration(total)
    };
  };

  /* Backen skaliert ueber Portionen, Cocktails ueber die Anzahl Drinks -
     das ist die richtige Metapher und der erste von sechs belegten
     Unterschieden. Bis 05.08.2026 bekamen Cocktails gar keinen Steller,
     sondern den Satz "Fuer 1 Drink". Wer fuer vier mixt, rechnet dann im
     Kopf mal vier - bei fuenf Zutaten. */
  const getYield = (facts, cocktail) => {
    if (cocktail) {
      const candidate = facts.find((text) => /(\d+)\s*(drinks?|cocktails?|gl(ä|ae)ser?|glas)\b/i.test(text));
      const match = candidate && candidate.match(/(\d+)\s*(drinks?|cocktails?|gl(?:ä|ae)ser?|glas)\b/i);
      const base = match ? Number(match[1]) || 1 : 1;
      return { base, unit: 'Drinks', unitOne: 'Drink', label: base + (base === 1 ? ' Drink' : ' Drinks') };
    }
    const candidate = facts.find((text) => /portion|stück|glas|person|ergibt/i.test(text));
    const match = candidate?.match(/(\d+(?:[.,]\d+)?)\s*(Portionen?|Stück|Gläser?|Personen?)/i);
    if (!match) return { base: 1, unit: 'Portion', label: 'Mengen wie im Rezept' };
    return {
      base: Number(match[1].replace(',', '.')) || 1,
      unit: match[2],
      label: `${match[1]} ${match[2]}`
    };
  };

  const normalizeLegacyRecipe = (card) => {
    const root = card.closest('[data-mag-article]');
    const cocktail = root?.dataset.articleFormat === 'cocktail';
    const title = clean(card.querySelector('.rc-title')?.textContent) ||
      clean(root?.querySelector('.mag-article__title')?.textContent);
    const description = clean(card.querySelector('.rc-desc')?.textContent);
    const facts = Array.from(card.querySelectorAll('.rc-meta li')).map((item) => clean(item.textContent)).filter(Boolean);
    const groups = [];
    let group = { title: '', items: [] };
    card.querySelectorAll('.rc-ing > li').forEach((item) => {
      if (item.classList.contains('rc-group')) {
        if (group.items.length) groups.push(group);
        group = { title: clean(item.textContent), items: [] };
      } else {
        group.items.push(parseIngredient(item));
      }
    });
    if (group.items.length) groups.push(group);

    /* Die Garnitur wandert aus der Liste heraus und schliesst sie ab. */
    let garnish = null;
    if (cocktail) {
      for (const current of groups) {
        const hit = current.items.findIndex((item) =>
          /garnitur|garnier|garnish|zum dekorieren/i.test(item.raw));
        if (hit >= 0) {
          garnish = current.items[hit];
          current.items.splice(hit, 1);
          break;
        }
      }
    }
    const steps = Array.from(card.querySelectorAll('.rc-steps > li')).map((item) => clean(item.textContent)).filter(Boolean);
    const tip = clean(card.querySelector('.rc-note')?.textContent);
    /* Zwei Felder, die beim Drink keine Schritte sind, sondern Attribute.

       Das Glas bestimmt, ob vorgekuehlt werden muss - eine Entscheidung
       vor dem Mixen. Die Garnitur bestimmt den Einkauf und muss
       bereitliegen. Beide Cocktail-Referenzen fuehren sie als eigene
       Felder; bis 05.08.2026 lagen sie hier als vierter Eckdatenpunkt
       ohne Beschriftung und als letzte Zutat im Fliesstext. */
    let glass = '';
    let visibleFacts = facts;
    if (cocktail) {
      const glassFact = facts.find((text) => {
        const [label, value] = factParts(text, 0);
        return /^glas$/i.test(clean(label)) || GLASS.test(value);
      });
      if (glassFact) {
        const parts = factParts(glassFact, 0);
        glass = clean(parts[1]);
        visibleFacts = facts.filter((text) => text !== glassFact);
      }
    }

    const yieldData = getYield(facts, cocktail);
    const times = timesFrom(facts);
    const originalId = card.id || 'rezept';

    const factMarkup = visibleFacts.map((fact, index) => {
      const [label, value] = factParts(fact, index);
      return `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`;
    }).join('');
    const groupMarkup = groups.map((current) => `
      ${current.title && groups.length > 1 ? `<h4 class="mag-recipe__group mag-kicker">${escapeHtml(current.title)}</h4>` : ''}
      <ul class="mag-recipe__list" role="list">
        ${current.items.map((item) => `
          <li class="mag-recipe__item">
            <label class="mag-recipe__check">
              <input type="checkbox" data-ingredient>
              ${item.qty ? `<span class="mag-recipe__qty"${item.numeric !== null ? ` data-qty="${item.numeric}"` : ''}>${escapeHtml(item.qty)}</span>` : ''}
              ${item.unit ? `<span class="mag-recipe__unit">${escapeHtml(item.unit)}</span>` : ''}
              <span class="mag-recipe__name">${escapeHtml(item.name)}</span>
            </label>
          </li>`).join('')}
      </ul>`).join('');
    const stepMarkup = steps.map((step, index) =>
      `<li class="mag-recipe__stepitem" id="step-${index + 1}"><p>${escapeHtml(step)}</p></li>`
    ).join('');
    const stepperLabel = cocktail ? 'Drinks' : 'Portionen';
    const servingsMarkup = `<div class="mag-recipe__servings" role="group" aria-label="${stepperLabel} anpassen">
          <button type="button" class="mag-recipe__step-btn" data-servings-down aria-label="Weniger ${escapeHtml(stepperLabel)}">−</button>
          <span class="mag-recipe__servings-val"><span data-servings>${yieldData.base}</span> <span data-servings-unit>${escapeHtml(yieldData.base === 1 ? singular(yieldData.unit) : yieldData.unit)}</span></span>
          <button type="button" class="mag-recipe__step-btn" data-servings-up aria-label="Mehr ${escapeHtml(stepperLabel)}">+</button>
        </div>`;

    const holder = document.createElement('div');
    holder.innerHTML = `
      <mag-recipe id="${escapeHtml(originalId)}" class="mag-scope mag-recipe mag-recipe--legacy${cocktail ? ' mag-recipe--cocktail' : ''}" data-base="${yieldData.base}">
        <div class="mag-recipe__inner">
          <header class="mag-recipe__head">
            <p class="mag-kicker">${cocktail ? 'Cocktail' : 'Rezept'}</p>
            <h2 class="mag-recipe__title">${escapeHtml(title)}</h2>
            ${description ? `<p class="mag-recipe__dek">${escapeHtml(description)}</p>` : ''}
            ${factMarkup ? `<dl class="mag-recipe__facts">${factMarkup}</dl>` : ''}
          </header>
          <div class="mag-recipe__toolbar">
            ${servingsMarkup}
            <div class="mag-recipe__tools">
              ${cocktail ? '<button type="button" class="mag-pill" data-unit-toggle aria-pressed="false">in cl</button>' : ''}
              <button type="button" class="mag-pill" data-cook-mode hidden>Display anlassen</button>
              <button type="button" class="mag-pill" data-print>Drucken</button>
            </div>
          </div>
          <div class="mag-recipe__cols">
            <div class="mag-recipe__ingredients">
              <h3 class="mag-recipe__subtitle">Zutaten</h3>
              ${glass ? `<dl class="mag-recipe__glass"><dt>Glas</dt><dd>${escapeHtml(glass)}</dd></dl>` : ''}
              ${groupMarkup}
              ${garnish ? `<dl class="mag-recipe__garnish"><dt>Garnitur</dt><dd>${escapeHtml(garnish.raw)}</dd></dl>` : ''}
            </div>
            <div class="mag-recipe__steps">
              <h3 class="mag-recipe__subtitle">${cocktail ? "So geht's" : 'Zubereitung'}</h3>
              <ol class="mag-recipe__steplist" role="list">${stepMarkup}</ol>
              ${tip ? `<aside class="mag-recipe__tip"><p class="mag-kicker">Tipp</p><p>${escapeHtml(tip)}</p></aside>` : ''}
            </div>
          </div>
        </div>
      </mag-recipe>`;
    const replacement = holder.firstElementChild;

    /* Namen als Knoten nachreichen, damit Links erhalten bleiben. Die
       Reihenfolge der Spannen entspricht der flachen Zutatenliste. */
    const flat = groups.flatMap((current) => current.items);
    replacement.querySelectorAll('.mag-recipe__name').forEach((span, index) => {
      const frag = flat[index] ? nameFragment(flat[index]) : null;
      if (!frag) return;
      span.textContent = '';
      span.appendChild(frag);
    });
    const dekSource = card.querySelector('.rc-desc');
    const dek = replacement.querySelector('.mag-recipe__dek');
    if (dek && dekSource) { dek.textContent = ''; dek.appendChild(sanitize(dekSource)); }

    card.replaceWith(replacement);

    document.querySelectorAll('style').forEach((style) => {
      if (style.textContent.includes('.recipe-card')) style.remove();
    });

    /* Strukturierte Daten: ergänzen statt ersetzen.

       Bis 05.08.2026 hat dieser Block jedes vorhandene Recipe-Objekt
       gelöscht und ein eigenes geschrieben. Dabei gingen Felder verloren,
       die im Artikel gepflegt sind - recipeCuisine, keywords,
       suitableForDiet, die Bild-Reihe, die Autorenseite - und der Autor
       stand fest auf "Marie", auch bei jedem anderen. Jetzt bleibt die
       serverseitige Auszeichnung führend und bekommt nur das dazu, was
       erst beim Umbau bekannt wird. */
    const nodes = Array.from(document.querySelectorAll('script[type="application/ld+json"]'));
    let host = null;
    let hostData = null;
    nodes.forEach((script) => {
      if (host) return;
      try {
        const data = JSON.parse(script.textContent);
        if (data && data['@type'] === 'Recipe') { host = script; hostData = data; }
      } catch (error) { /* andere strukturierte Daten unangetastet lassen */ }
    });

    /* Verklammerung fuer maschinelles Lesen: ohne @id und
       mainEntityOfPage steht das Rezept unverbunden neben dem
       BlogPosting, das dieselbe URL beschreibt. */
    const pageUrl = location.origin + location.pathname;
    const fromModule = {
      '@context': 'https://schema.org',
      '@type': 'Recipe',
      '@id': pageUrl + '#recipe',
      mainEntityOfPage: pageUrl,
      publisher: { '@type': 'Organization', name: 'as.if.records', url: location.origin },
      inLanguage: document.documentElement.lang || 'de-DE',
      name: title,
      image: document.querySelector('meta[property="og:image"]')?.content || undefined,
      author: { '@type': 'Person', name: clean(root?.querySelector('[rel="author"], .mag-author__name')?.textContent) || undefined },
      datePublished: root?.querySelector('time[datetime]')?.dateTime || undefined,
      description: description || clean(root?.querySelector('.mag-article__lede')?.textContent),
      prepTime: times.prepTime,
      cookTime: times.cookTime,
      totalTime: times.totalTime,
      recipeYield: yieldData.label,
      recipeCategory: cocktail ? 'Cocktail' : 'Rezept',
      recipeIngredient: groups.flatMap((current) => current.items.map((item) => item.raw)),
      recipeInstructions: steps.map((text, index) => ({
        '@type': 'HowToStep',
        text,
        url: `${location.origin}${location.pathname}#step-${index + 1}`
      }))
    };
    if (!fromModule.author.name) delete fromModule.author;

    if (host && hostData) {
      Object.keys(fromModule).forEach((key) => {
        const value = fromModule[key];
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value) && value.length === 0) return;
        const existing = hostData[key];
        const empty = existing === undefined || existing === null || existing === '' ||
          (Array.isArray(existing) && existing.length === 0);
        /* Zutaten und Schritte kommen aus dem umgebauten Modul, weil sie
           dort auf denselben Sprungmarken sitzen wie im Text. */
        if (empty || key === 'recipeIngredient' || key === 'recipeInstructions') {
          hostData[key] = value;
        }
      });
      host.textContent = JSON.stringify(hostData);
      host.dataset.magRecipeJson = 'kept';
    } else {
      const ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.dataset.magRecipeJson = 'true';
      ld.textContent = JSON.stringify(fromModule);
      document.head.append(ld);
    }
    return replacement;
  };

  /* Die Reihenfolge im Dokument ist die Reihenfolge fürs Auge, für die
     Tabulatortaste und für den Screenreader. Sie darf nicht per CSS
     auseinanderlaufen - WCAG 2.2 SC 1.3.2 und 2.4.3, beide Level A.
     Falls ein Rezeptmodul weit hinten im Text landet, wandert es hier
     hinter den Einstieg. Steht es schon vorne, passiert nichts. */
  const placeRecipe = (node) => {
    const body = document.querySelector('[data-article-body]');
    if (!body || !node || node.parentElement !== body) return;
    const kids = Array.from(body.children);
    const index = kids.indexOf(node);
    if (index <= 3) return;
    let anchor = null;
    for (const child of kids) {
      if (child === node) break;
      if (child.tagName === 'P' && clean(child.textContent).length > 60) { anchor = child; break; }
    }
    if (!anchor) return;
    const figure = anchor.nextElementSibling;
    if (figure && figure.tagName === 'FIGURE') anchor = figure;
    if (anchor.nextElementSibling === node) return;
    anchor.after(node);
  };


  /* ---------------------------------------------------------------- Bewertung

     Fuenf Sterne, eine Stimme je Browser und Rezept, Speicher in einem
     eigenen Dienst (ratings.as-if-records.com, Cloudflare Worker mit D1).

     Zwei Regeln, die aus der Recherche kommen und hier nicht verhandelbar
     sind: es gibt keinen Startwert - was nicht geklickt wurde, existiert
     nicht -, und der Schnitt erscheint erst ab der Mindestzahl, die der
     Dienst mitliefert. aggregateRating wird genau dann und nur dann in
     die Auszeichnung geschrieben.

     Faellt der Dienst aus, verschwindet der ganze Block. Ein Bedienelement
     ohne Funktion ist schlimmer als keins.
  */

  const bootRating = () => {
    const box = document.querySelector('[data-recipe-rating]');
    if (!box) return;
    const handle = box.dataset.handle;
    const base = (box.dataset.endpoint || '').replace(/\/+$/, '');
    if (!handle || !base) return;

    const stars = Array.from(box.querySelectorAll('[data-star]'));
    const group = box.querySelector('[role="radiogroup"]');
    const countEl = box.querySelector('[data-rating-count]');
    const liveEl = box.querySelector('[data-rating-live]');
    let state = { count: 0, average: null, minimum: 5, mine: null };

    const paint = () => {
      const shown = state.mine || 0;
      stars.forEach((btn) => {
        const value = Number(btn.dataset.star);
        btn.setAttribute('aria-checked', value === state.mine ? 'true' : 'false');
        btn.classList.toggle('is-on', value <= shown);
        btn.tabIndex = (state.mine ? value === state.mine : value === 1) ? 0 : -1;
      });
      box.classList.toggle('is-rated', !!state.mine);
      if (!countEl) return;
      if (state.average !== null) {
        countEl.textContent = String(state.average).replace('.', ',') +
          ' von 5 · ' + state.count + (state.count === 1 ? ' Stimme' : ' Stimmen');
      } else if (state.count > 0) {
        countEl.textContent = state.count + (state.count === 1 ? ' Stimme' : ' Stimmen') +
          ' · Schnitt ab ' + state.minimum;
      } else {
        countEl.textContent = 'Noch keine Stimme';
      }
    };

    /* aggregateRating gehoert in dasselbe Recipe-Objekt, das die Seite
       ohnehin ausliefert - nicht in ein zweites daneben. */
    const writeSchema = () => {
      const node = Array.from(document.querySelectorAll('script[type="application/ld+json"]'))
        .find((s) => { try { return JSON.parse(s.textContent)['@type'] === 'Recipe'; } catch (e) { return false; } });
      if (!node) return;
      let data;
      try { data = JSON.parse(node.textContent); } catch (e) { return; }
      if (state.average !== null && state.count >= state.minimum) {
        data.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: state.average,
          ratingCount: state.count,
          bestRating: 5,
          worstRating: 1
        };
      } else {
        delete data.aggregateRating;
      }
      node.textContent = JSON.stringify(data);
    };

    const apply = (data) => {
      state = {
        count: Number(data.count) || 0,
        average: data.average === null || data.average === undefined ? null : Number(data.average),
        minimum: Number(data.minimum) || 5,
        mine: data.mine === null || data.mine === undefined ? null : Number(data.mine)
      };
      box.hidden = false;
      paint();
      writeSchema();
    };

    /* Waehrend gesendet wird darf kein Knopf disabled werden. Ein disabled
       Button verliert den Fokus ans Dokument, und der naechste Tab beginnt
       wieder am Seitenanfang (SC 2.4.3). Stattdessen aria-busy an der Gruppe
       und eine Sperre in der Funktion selbst. */
    let busy = false;
    const send = (value) => {
      if (busy) return;
      busy = true;
      if (group) group.setAttribute('aria-busy', 'true');
      fetch(base + '/r/' + encodeURIComponent(handle), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ rating: value })
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((data) => {
          apply(data);
          if (liveEl) liveEl.textContent = 'Danke. ' + value + ' von 5 Sternen gespeichert.';
        })
        .catch(() => {
          if (liveEl) liveEl.textContent = 'Die Bewertung konnte nicht gespeichert werden.';
        })
        .finally(() => {
          busy = false;
          if (group) group.removeAttribute('aria-busy');
        });
    };

    stars.forEach((btn, index) => {
      btn.addEventListener('click', () => send(Number(btn.dataset.star)));
      btn.addEventListener('keydown', (event) => {
        /* Pfeiltasten bewegen nur den Fokus. Abgestimmt wird mit Enter oder
           Leertaste - sonst gibt jeder Tastendruck auf dem Weg zu Stern 5
           eine eigene Stimme ab. */
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
          : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
        if (step) {
          event.preventDefault();
          const next = stars[(index + step + stars.length) % stars.length];
          stars.forEach((s) => { s.tabIndex = -1; });
          next.tabIndex = 0;
          next.focus();
          return;
        }
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          send(Number(btn.dataset.star));
        }
      });
    });

    fetch(base + '/r/' + encodeURIComponent(handle), { headers: { accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(apply)
      .catch(() => { /* Dienst nicht erreichbar - der Block bleibt aus. */ });
  };

  const boot = () => {
    const legacyCards = document.querySelectorAll('[data-article-body] .recipe-card');
    const structuredCard = document.querySelector('mag-recipe:not(.mag-recipe--legacy)');
    if (structuredCard) {
      legacyCards.forEach((card) => card.remove());
      return;
    }
    legacyCards.forEach((card) => placeRecipe(normalizeLegacyRecipe(card)));
  };

  const bootAll = () => { boot(); bootRating(); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll, { once: true });
  } else {
    bootAll();
  }
})();
